/**
 * Who gets the guided demo — the rule alone, importing nothing.
 *
 * Same reasoning as onboarding/eligibility.ts: useDemoGuideGate reaches
 * useAuth, which transitively touches `window` at import time, and vitest runs
 * in node. Keeping the predicate import-free is what makes
 * __tests__/gateExclusivity.test.ts possible.
 */

export type DemoGuideEligibilityInput = {
  /**
   * Has Keycloak finished deciding? `authenticated` means nothing until it has.
   *
   * The store starts at `{ keycloakInstance: null, authenticated: false }` and
   * initKeycloak only writes the real answer when check-sso resolves, so for
   * the whole of that window a signed-in user is indistinguishable from a
   * logged-out one by `authenticated` alone.
   */
  authResolved: boolean;
  authenticated: boolean;
  /**
   * Viewport wide enough to run the flow. See shared/hooks/useGuideViewport.
   *
   * Passed in rather than measured here so this file stays import-free and
   * testable under vitest's node environment.
   */
  viewportSupported: boolean;
  /**
   * Has this browser ever signed in? See utils/priorAuth.
   *
   * Passed in rather than read here so this file stays import-free.
   */
  hasPriorAuth: boolean;
};

/**
 * Logged-out visitors only, and only once we know they are.
 *
 * Deliberately `!authenticated` rather than `isInDemoMode`. That flag is also
 * true for a signed-in subscriber whose access has lapsed, and someone who has
 * already paid does not need to be told what the product is — they need the
 * renewal prompt, which SubscriptionBanner already gives them. Showing them a
 * "what do you want to see?" chooser would read as the product forgetting who
 * they are.
 *
 * `authResolved` is the other half of that, and it was missing. Treating "not
 * known yet" as "logged out" opened the picker on every single load before
 * check-sso came back — over the top of a signed-in user's dashboard, with
 * `begin()` writing demo state into their localStorage and reporting a
 * picker_shown for them on the way past. Silent, because the modal vanishes
 * the moment the token lands, so it only looks like a flicker unless
 * check-sso is slow — and then it is a chooser sitting on a paid account.
 *
 * A failed init still counts as resolved: the app treats that visitor as
 * logged out everywhere else, and the demo is the right thing to show someone
 * who cannot sign in.
 *
 * This is also the entire reason the demo guide and the onboarding welcome
 * flow can never collide: onboarding needs `authenticated`, this needs the
 * opposite.
 *
 * The viewport check is the third gate and the newest. The run was startable
 * on a phone and unfinishable on one — the chooser is responsive, everything
 * after it is `hidden sm:block` — so a phone visitor could hand over their
 * asset class, have a dashboard applied, and be left with no guidance and no
 * way back into the run.
 */
export function isDemoGuideEligible(
  input: DemoGuideEligibilityInput
): boolean {
  if (!input.authResolved) return false;
  if (!input.viewportSupported) return false;
  // Someone who has signed in here before is a customer between sessions, not
  // a visitor who needs the product explained. They are logged out by every
  // other measure, which is how they ended up meeting the chooser on their way
  // to the login button.
  if (input.hasPriorAuth) return false;
  return !input.authenticated;
}
